/**
 * Sentry integration for Constrigo ops app (Sentry project: "ops")
 *
 * Setup:
 *   1. @sentry/react is a real dependency of this app.
 *   2. Set VITE_SENTRY_DSN in apps/ops/.env  (DSN of the "ops" Sentry project)
 *   3. Set VITE_SENTRY_ENVIRONMENT to one of: development | uat | production
 *      (defaults to Vite's MODE when unset — set it explicitly to "uat" on the uat build).
 *   4. initSentry() is called at the top of main.tsx before rendering.
 *
 * Coverage:
 *   - Crashes / render errors      → <Sentry ErrorBoundary> + captureException()
 *   - Uncaught promise rejections  → captured automatically by the SDK
 *   - console.info/warn/error      → forwarded to Sentry Logs (all levels)
 *   - Structured logs              → log.info/warn/error/fatal() → Sentry Logs
 *   - Every event carries the connected user + current route + full context.
 */
import * as Sentry from "@sentry/react";

/** The Sentry project name this app reports as. */
const PROJECT = "ops";

function sentryEnv(): string {
  return (
    (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ||
    import.meta.env.MODE ||
    "development"
  );
}

const isProd = (): boolean => sentryEnv() === "production";

// ─── PII scrubbing ─────────────────────────────────────────────────────────────
const SENSITIVE =
  /^(password|pass|token|refreshToken|accessToken|authorization|cookie|otp|code|verifCode|iban|rib|siret|captchaToken)$/i;
const PARTIAL_MASK = /^(email|phone|mobile|telephone|tel)$/i;

function scrub(obj: unknown, depth = 0): unknown {
  if (depth > 6 || obj == null) return obj;
  if (Array.isArray(obj)) return obj.map((v) => scrub(v, depth + 1));
  if (typeof obj === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
      if (SENSITIVE.test(k)) out[k] = "[REDACTED]";
      else if (PARTIAL_MASK.test(k) && typeof v === "string") {
        out[k] = v.length > 4 ? `${v.slice(0, 2)}***${v.slice(-2)}` : "[REDACTED]";
      } else out[k] = scrub(v, depth + 1);
    }
    return out;
  }
  return obj;
}

function maskEmail(email: string | undefined | null): string | undefined {
  if (!email) return undefined;
  const [local, domain] = email.split("@");
  if (!domain) return email.length > 4 ? `${email.slice(0, 2)}***` : "[REDACTED]";
  const maskedLocal = local.length > 2 ? `${local.slice(0, 2)}***` : "***";
  return `${maskedLocal}@${domain}`;
}

// ─── Init ──────────────────────────────────────────────────────────────────────
export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) return;

  Sentry.init({
    dsn,
    environment: sentryEnv(),
    tracesSampleRate: isProd() ? 0.1 : 1.0,
    // Sentry Logs (v10): deliver log.* and console.* forwards.
    enableLogs: true,
    integrations: [
      Sentry.browserTracingIntegration(),
      // Mirror console.info/warn/error into Sentry Logs (all levels).
      Sentry.consoleLoggingIntegration({ levels: ["info", "warn", "error"] }),
    ],
    sendDefaultPii: false,
    initialScope: {
      // Redundant classification tag (DSN already routes to the "ops" project).
      tags: { "app.project": PROJECT },
    },
    beforeSend(event) {
      const e = event as unknown as { request?: unknown; contexts?: unknown; extra?: unknown };
      if (e.request) e.request = scrub(e.request);
      if (e.contexts) e.contexts = scrub(e.contexts);
      if (e.extra) e.extra = scrub(e.extra);
      return event;
    },
  });
}

// ─── Connected user ────────────────────────────────────────────────────────────
export interface SentryUser {
  id: string;
  email?: string;
  role?: string;
  companyId?: string;
}

/** Attach the connected user so every event names WHO it happened to. */
export function setSentryUser(user: SentryUser | null | undefined): void {
  if (!user) {
    Sentry.setUser(null);
    return;
  }
  Sentry.setUser({
    id: user.id,
    username: maskEmail(user.email),
    segment: user.role,
  });
  if (user.companyId) Sentry.setTag("company.id", user.companyId);
  if (user.role) Sentry.setTag("user.role", user.role);
}

export function clearSentryUser(): void {
  Sentry.setUser(null);
}

// ─── Manual capture (with location context) ────────────────────────────────────
/**
 * Capture an exception with a clear message that names WHERE (current route)
 * it happened. Extra context is merged and scrubbed.
 */
export function captureException(err: unknown, ctx?: Record<string, unknown>): void {
  const pathname = typeof window !== "undefined" ? window.location.pathname : undefined;
  Sentry.captureException(err, {
    tags: { route: pathname },
    contexts: { location: { pathname, href: typeof window !== "undefined" ? window.location.href : undefined } },
    ...(ctx as object),
  });
}

// ─── Structured logs (all levels) ──────────────────────────────────────────────
function withMeta(attributes?: Record<string, unknown>): Record<string, unknown> {
  return {
    app: PROJECT,
    route: typeof window !== "undefined" ? window.location.pathname : undefined,
    ...(attributes ? (scrub(attributes) as Record<string, unknown>) : {}),
  };
}

/** Send structured logs to Sentry Logs (project "ops"), all levels. */
export const log = {
  info: (message: string, attributes?: Record<string, unknown>) =>
    Sentry.logger.info(message, withMeta(attributes)),
  warn: (message: string, attributes?: Record<string, unknown>) =>
    Sentry.logger.warn(message, withMeta(attributes)),
  error: (message: string, attributes?: Record<string, unknown>) =>
    Sentry.logger.error(message, withMeta(attributes)),
  fatal: (message: string, attributes?: Record<string, unknown>) =>
    Sentry.logger.fatal(message, withMeta(attributes)),
};

/**
 * Run an async operation and report any thrown error to Sentry (with route +
 * context), returning `undefined` on failure. Use to replace catch blocks that
 * would otherwise swallow errors silently.
 *
 * @example
 *   const data = await tryCatch(() => fetchPdf(id), { id });
 */
export async function tryCatch<T>(
  fn: () => Promise<T>,
  ctx?: Record<string, unknown>,
): Promise<T | undefined> {
  try {
    return await fn();
  } catch (err) {
    captureException(err, ctx ? { extra: ctx } : undefined);
    return undefined;
  }
}

/** Report an already-caught error to Sentry with optional context. */
export function reportError(err: unknown, ctx?: Record<string, unknown>): void {
  captureException(err, ctx ? { extra: ctx } : undefined);
}

/**
 * Report a react-query / tRPC error to Sentry, skipping expected control-flow
 * HTTP errors (401/403/404/409/429) that the UI already handles and which would
 * otherwise be noise. Wire this into QueryCache / MutationCache `onError` for
 * central, project-wide capture of all data/mutation failures.
 */
export function reportQueryError(error: unknown, ctx?: Record<string, unknown>): void {
  const status = (error as { data?: { httpStatus?: number } })?.data?.httpStatus;
  // Expected client-side errors (4xx: validation/auth/not-found/rate-limit) go
  // to Sentry Logs at warn level (searchable) but do NOT raise an Issue/alert.
  // Only real failures (5xx, network, non-HTTP throws) become Issues.
  if (typeof status === "number" && status >= 400 && status < 500) {
    const message = error instanceof Error ? error.message : String(status);
    log.warn(`[query ${status}] ${message}`, { ...ctx, status });
    return;
  }
  captureException(error, ctx ? { extra: ctx } : undefined);
}

export { Sentry };
